import { atom } from "jotai";

import { reorderPrivateRounds } from "@/features/BookingRounds/utils/reorderPrivateRounds";

import { UpdateSingleSlotBookingInput } from "../../../../hooks/saveChangesHooks/useSaveChangesToSingleSlotBooking/__generated__/useSaveChangesToSingleSlotBookingMutation.graphql";
import {
  currentBookingAtom,
  originalBookingOwnerClientIdAtom,
} from "../../../stores";
import { updatedCustomerRoundsAtom } from "../UpdatedRounds/UpdatedCustomerRounds/UpdatedCustomerRoundsAtom";
import { updatedGuestRoundsAtom } from "../UpdatedRounds/UpdatedGuestRoundsAtom/UpdatedGuestRoundsAtom";
import { updatedMemberRoundsAtom } from "../UpdatedRounds/UpdatedMemberRoundsAtom/UpdatedMemberRoundsAtom";
import { updatedNewGuestRoundsAtom } from "../UpdatedRounds/UpdatedNewGuestRoundsAtom/UpdatedNewGuestRoundsAtom";

const guestTypes = ["guest", "unknownGuest", "newGuest"];

export const updateGolfBookingPayloadAtom =
  atom<UpdateSingleSlotBookingInput | null>((get) => {
    const currentBooking = get(currentBookingAtom);
    if (!currentBooking) return null;
    const isPrivate = currentBooking.isPrivate;
    const updatedCustomerRounds = get(updatedCustomerRoundsAtom);
    const updatedMemberRounds = get(updatedMemberRoundsAtom);
    const updatedGuestRounds = get(updatedGuestRoundsAtom);
    const updatedNewGuestRounds = get(updatedNewGuestRoundsAtom);
    const ownerRound = currentBooking.rounds.find((r) => r.isOwner);
    const ownerClientId =
      ownerRound?.player?.id ?? get(originalBookingOwnerClientIdAtom);
    const hasExplicitGuestHost = currentBooking.rounds.some(
      (round) =>
        guestTypes.includes(round.player?.clientType ?? "") && !!round.hostId
    );

    // For DRAFT private bookings (e.g. each draft created by the multi-tee-time
    // "Reserve Times" flow) we mirror the wizard's create-flow behavior and
    // reorder rounds so guests sit immediately after the owner. This avoids
    // the backend's `member_hold_host` validation when a Member Hold was added
    // before a guest in the user's tap order.
    //
    // Note: we deliberately do NOT require ownerClientId here. The 2nd+
    // drafts from multi-tee-time creation ship with zero rounds, so no round
    // has `isOwner: true` yet. The reorder utility still produces a valid
    // order in that case — guests first, Member Hold last — which the backend
    // accepts (guests get `host_id: nil`, no Member Hold is inferred as host).
    //
    // Explicit host assignments are handled by setHostIdAndIndexOnRoundAtom,
    // which updates each round's index before these payload selectors run.
    const shouldReorderForDraft =
      isPrivate &&
      currentBooking.status === "DRAFT" &&
      !currentBooking.rounds.some((round) => round.bookingIndex != null) &&
      !hasExplicitGuestHost &&
      !!updatedMemberRounds &&
      !!updatedGuestRounds &&
      !!updatedNewGuestRounds;

    const reordered = shouldReorderForDraft
      ? reorderPrivateRounds({
          memberRounds: updatedMemberRounds,
          guestRounds: updatedGuestRounds,
          newGuestRounds: updatedNewGuestRounds,
          ownerClientId: ownerClientId ?? "",
        })
      : null;

    const input: UpdateSingleSlotBookingInput = {
      bookingId: currentBooking.id,
      notifyMember: true,
      customerRounds: isPrivate ? undefined : updatedCustomerRounds,
      memberRounds: isPrivate
        ? reordered?.memberRounds ?? updatedMemberRounds
        : undefined,
      guestRounds: isPrivate
        ? reordered?.guestRounds ?? updatedGuestRounds
        : undefined,
      newGuestRounds: isPrivate
        ? reordered?.newGuestRounds ?? updatedNewGuestRounds
        : undefined,
      ownerClientId: ownerClientId ?? undefined,
      request: currentBooking.request
        ? {
            time: currentBooking.request.time || "",
            minTime: currentBooking.request.minTime || "",
            maxTime: currentBooking.request.maxTime || "",
          }
        : undefined,
    };

    return input;
  });
